import type { RoadmapContext } from '../exposes/AIAssistant';
import { createAuthJsonHeaders } from './authBridge';
import { isMockEnabled, deliverMockStreamResponse } from './streamChatMock';
import { getStreamUrl } from '../utils/config';

export interface StreamChatParams {
  message: string;
  conversationId: string | null;
  sessionId: string | null;
  context: RoadmapContext;
  onRequestId?: (requestId: string) => void;
  onMessageStart: (messageId: string) => void;
  onToken: (delta: string) => void;
  onToolStart?: (tool: string) => void;
  onThinking?: (content: string) => void;
  onDone: (conversationId: string, runtimeSessionId: string) => void;
  onError: (error: string) => void;
  signal: AbortSignal;
}

export async function streamChat(params: StreamChatParams) {
  if (isMockEnabled()) {
    deliverMockStreamResponse(params);
    return;
  }

  const {
    message,
    conversationId,
    sessionId,
    context,
    onMessageStart,
    onToken,
    onDone,
    onError,
    signal
  } = params;

  const headers = await createAuthJsonHeaders(
    sessionId ? { 'X-Runtime-Session-Id': sessionId } : {}
  );

  fetch(getStreamUrl(), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify({ message, conversationId, context }),
    signal
  })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const newRuntimeSessionId =
        response.headers.get('X-Runtime-Session-Id') ?? '';
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';
      let streamCompleted = false;

      function processLines(text: string) {
        buffer += text;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (currentEvent) {
                case 'agent_invoke_context':
                  params.onRequestId?.(data.requestId);
                  break;
                case 'message_start':
                  onMessageStart(data.messageId);
                  break;
                case 'token':
                  onToken(data.delta);
                  break;
                case 'thinking':
                  params.onThinking?.(data.content);
                  break;
                case 'tool_start':
                  params.onToolStart?.(data.tool);
                  break;
                case 'done':
                  streamCompleted = true;
                  onDone(data.conversationId, newRuntimeSessionId);
                  break;
                case 'error':
                  streamCompleted = true;
                  onError(data.message || 'Stream error');
                  break;
              }
            } catch {
              /* non-JSON line, skip */
            }
            currentEvent = '';
          }
        }
      }

      function read(): Promise<void> {
        return reader!.read().then(({ done, value }) => {
          if (done) {
            // Flush any bytes the decoder held back for multi-byte chars
            const tail = decoder.decode(undefined, { stream: false });
            if (tail) processLines(tail);
            // If the server closed without a done/error event, surface an error
            // so the saga can clear isStreaming and re-enable the composer.
            if (!streamCompleted) {
              onError('Stream ended without a completion signal');
            }
            return;
          }
          processLines(decoder.decode(value, { stream: true }));
          return read();
        });
      }

      return read();
    })
    .catch(err => {
      if (err.name === 'AbortError') return;
      onError(err.message || 'Failed to connect to AI assistant');
    });
}
